import { useCallback, useEffect, useState } from 'react';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

const getIsDismissed = () => {
	const now = Date.now();
	const value = localStorage?.getItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_MOBILE_APP_PROMO_DISMISSED,
	);

	// Permanently dismissed
	if (value === 'true') {
		return { isDismissed: true, isAutoDismissed: false };
	}

	// Value is not set or blank
	if (!value || value.trim() === '') {
		return { isDismissed: false, isAutoDismissed: false };
	}

	// Check if the value is a first-seen timestamp
	const firstSeenDate = parseInt(value, 10);
	const isTimestamp = !isNaN(firstSeenDate);
	const isExpired = isTimestamp && now - firstSeenDate > TWO_WEEKS_MS;

	if (isTimestamp && !isExpired) {
		return { isDismissed: false, isAutoDismissed: false };
	}

	// Default: timestamp exceeded 2 weeks or is unrecognizable (corrupted) — dismiss
	localStorage?.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_MOBILE_APP_PROMO_DISMISSED, 'true');
	// Only fire auto-dismissal analytics if it was a legitimate time-based expiry
	return { isDismissed: true, isAutoDismissed: isExpired };
};

const onMobileAppPromoView = () => {
	const now = Date.now();
	const value = localStorage?.getItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_MOBILE_APP_PROMO_DISMISSED,
	);
	// Only record the first-seen timestamp if nothing is stored yet
	if (!value) {
		localStorage?.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_MOBILE_APP_PROMO_DISMISSED,
			now.toString(),
		);
	}
};

export const useMobileAppPromoDismiss = (options?: { onAutoDismiss?: () => void }) => {
	const [{ isDismissed, isAutoDismissed }, setDismissState] = useState(getIsDismissed);

	useEffect(() => {
		if (isAutoDismissed) {
			options?.onAutoDismiss?.();
		}
		// We only want to run this once on mount when the state is initialized
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onMobileAppPromoDismiss = useCallback(() => {
		localStorage?.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_MOBILE_APP_PROMO_DISMISSED, 'true');
		setDismissState({ isDismissed: true, isAutoDismissed: false });
	}, []);

	return {
		isMobileAppPromoDismissed: isDismissed,
		onMobileAppPromoDismiss,
		onMobileAppPromoView,
	};
};
